import React, { useState, useEffect, ReactNode, HTMLAttributes } from 'react'
import { KnownTarget } from 'styled-components/dist/types'

import useComponentSize from '../../hooks/useComponentSize'
import Tooltip, { TooltipProps, TooltipTrigger } from '../Tooltip'
import { TypographyProps } from '../Typography'
import { DynamicTooltipTypographyContainer, DynamicTypography } from './styles'

export interface DynamicTooltipTypographyProps extends HTMLAttributes<HTMLElement> {
  tooltip?: TooltipProps & { content?: ReactNode; alwaysEnabled?: boolean }
  typography?: TypographyProps & { as?: KnownTarget }
  lineClamp?: number
  maxWidth?: number
  onOverflowChange?: (isOverflowing: boolean) => void
  tooltipAlwayEnable?: boolean
  widthOverflowOnly?: boolean
}

const DynamicTooltipTypography: React.FC<DynamicTooltipTypographyProps> = ({
  children,
  lineClamp = 1,
  maxWidth,
  typography: { as, ...typography } = {},
  tooltip: { alwaysEnabled, ...tooltip } = {},
  widthOverflowOnly,
  onOverflowChange,
  ...otherProps
}) => {
  const [ref, size] = useComponentSize<HTMLSpanElement>()
  const [overflowing, setOverflowing] = useState(!!alwaysEnabled)

  useEffect(() => {
    if (!alwaysEnabled && ref.current) {
      const widthOverflow = ref.current.offsetWidth < ref.current.scrollWidth
      const heightOverflow = ref.current.offsetHeight < ref.current.scrollHeight

      setOverflowing(widthOverflowOnly ? widthOverflow : widthOverflow || heightOverflow)
    }
  }, [ref.current, size, widthOverflowOnly])

  useEffect(() => {
    if (!alwaysEnabled && onOverflowChange) onOverflowChange(overflowing)
  }, [alwaysEnabled, onOverflowChange, overflowing])

  return (
    <Tooltip
      open={overflowing ? undefined : false}
      description={tooltip.title || tooltip.content ? undefined : children}
      data-is-sensitive={typography?.['data-is-sensitive']}
      hasArrow={tooltip.hasArrow ?? true}
      offsetValue={tooltip.offsetValue ?? 4}
      placement={tooltip.placement ?? 'top'}
      {...tooltip}
    >
      <DynamicTooltipTypographyContainer as={as} {...otherProps}>
        <TooltipTrigger asChild>
          <DynamicTypography as="span" {...typography} $lineClamp={lineClamp} $maxWidth={maxWidth}>
            <span ref={ref} className="dynamic-typography-wrapper">
              {children || tooltip?.title || tooltip?.description}
            </span>
          </DynamicTypography>
        </TooltipTrigger>
      </DynamicTooltipTypographyContainer>
    </Tooltip>
  )
}

export default DynamicTooltipTypography
